import { zodResolver } from '@hookform/resolvers/zod';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useMutation } from '@tanstack/react-query';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { Calendar, CircleAlert, StarsIcon, UserIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z, ZodIssueCode } from 'zod';
import queryClient from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import i18n from '@/Config/app/i18n';
import { GuidelineDto, HandoffDto } from '@/Domain/api/Action/dto/Handoff.dto';
import { User } from '@/Domain/api/User/dto/User.dto';
import { EmailNotificationField } from '@/Domain/components/Action/HandoffAction/Form/Field/EmailNotificationField';
import { HandoffRulesField } from '@/Domain/components/Action/HandoffAction/Form/Field/HandoffRulesField';
import { HumanAvailabilityField } from '@/Domain/components/Action/HandoffAction/Form/Field/HumanAvailabilityField';
import { KeywordsField } from '@/Domain/components/Action/HandoffAction/Form/Field/KeywordsField';
import { TopicRoutingField } from '@/Domain/components/Action/HandoffAction/Form/Field/TopicRoutingField';
import { buildRoutingProfiles } from '@/Domain/components/Action/HandoffAction/Form/Field/buildRoutingProfiles';
import { HandoffActionGraphic } from '@/Domain/components/Action/HandoffAction/HandoffAction.graphic';
import {
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/Domain/components/Shared/AlertDialog/AlertDialog';
import { Banner } from '@/Domain/components/Shared/Banner/Banner';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/Domain/components/Shared/Form/Form';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import { AutosizeTextarea } from '@/Domain/components/Shared/Textarea/Textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';
import CloseIcon from '@/Domain/components/icons/Close.icon';
import { AppHelper } from '@/Domain/helper/App.helper';
import { useScenarioUsers } from '@/Domain/hooks/AI/useScenarioUsers.hook';
import { useActiveSection } from '@/Domain/hooks/App/useActiveSection.hook';
import { CAL_LINK, useCalModal } from '@/Domain/hooks/useCalModal.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';

const handoffFormSchema = z
  .object({
    availability: z.array(
      z.object({
        id: z.number().nullable(),
        day_span: z.string(),
        start_time: z.string(),
        end_time: z.string(),
      }),
    ),
    generic_guidelines: z.array(
      z.object({
        name: z.string().min(1),
        description: z.string().min(1),
        is_active: z.boolean(),
      }),
    ),
    custom_guidelines: z.array(
      z.object({
        id: z.number().nullable(),
        name: z.string().min(1).max(100),
        description: z.string().min(1).max(2000),
        is_active: z.boolean(),
      }),
    ),
    routing_profiles: z.array(
      z.object({
        user_id: z.number(),
        routing_description: z.string().max(150),
        is_active: z.boolean(),
      }),
    ),
    settings: z.object({
      question_message: z.string().min(1).max(256),
      confirmation_message: z.string().min(1).max(500),
      confirmation_outside_working_hours_message: z.string().max(500),
      timezone: z.string(),
      is_active: z.boolean(),
      send_email_notification: z.boolean(),
      ai_summary_active: z.boolean(),
      working_hours_active: z.boolean(),
      keywords: z.array(z.object({ value: z.string() })),
      emails: z.array(
        z.object({
          value: z.string().email(),
        }),
      ),
    }),
  })
  .superRefine((data, ctx) => {
    const settings = data.settings;

    if (settings.working_hours_active && !settings.confirmation_outside_working_hours_message) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: i18n.t(
          'handoff_action:form_validation.required_confirmation_outside_working_hours_message',
        ),
        path: ['settings', 'confirmation_outside_working_hours_message'],
      });
    }

    data.routing_profiles.forEach((profile, index) => {
      if (profile.is_active && !profile.routing_description.trim()) {
        ctx.addIssue({
          code: ZodIssueCode.custom,
          message: i18n.t(
            'handoff_action:form_validation.routing_description_required_when_active',
          ),
          path: ['routing_profiles', index, 'routing_description'],
        });
      }
    });
  });

export type HandoffForm = z.infer<typeof handoffFormSchema>;

const buildHandoffFormValues = (handoff: HandoffDto, users: User[] | undefined): HandoffForm => ({
  availability: handoff.availability || [],
  generic_guidelines: handoff.generic_guidelines || [],
  custom_guidelines:
    handoff.custom_guidelines.map((guideline: GuidelineDto) => ({
      ...guideline,
      name: AppHelper.humanizeSnakeCase(guideline.name),
    })) || [],
  routing_profiles: buildRoutingProfiles(handoff.routing_profiles || [], users),
  settings: {
    question_message: handoff.settings.question_message || '',
    confirmation_message: handoff.settings.confirmation_message || '',
    confirmation_outside_working_hours_message:
      handoff.settings.confirmation_outside_working_hours_message || '',
    timezone: handoff.settings.timezone || '',
    is_active: handoff.settings.is_active || false,
    send_email_notification: handoff.settings.send_email_notification || false,
    ai_summary_active: handoff.settings.ai_summary_active || false,
    working_hours_active: handoff.settings.working_hours_active || false,
    keywords: handoff.settings.keywords.map((keyword) => ({ value: keyword })) || [],
    emails: handoff.settings.emails.map((keyword) => ({ value: keyword })) || [],
  },
});

type Props = {
  handoff: HandoffDto;
};

export const HandoffActionSheet = ({ handoff }: Props) => {
  const { t } = useTranslation('handoff_action');
  const { isOpen, closeModal } = useModalStore();
  const { users, isLoading: usersLoading } = useScenarioUsers();
  const openCalModal = useCalModal();

  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [scrollContainer, setScrollContainer] = useState<HTMLElement | null>(null);
  const [indicatorTop, setIndicatorTop] = useState(0);

  const sections = useMemo(
    () => [
      { id: 'question', label: i18n.t('handoff_action:menu.question') },
      { id: 'confirmation', label: i18n.t('handoff_action:menu.confirmation') },
      { id: 'human_availability', label: i18n.t('handoff_action:menu.availability') },
      { id: 'handoff_rules', label: i18n.t('handoff_action:menu.handoff_rules') },
      // { id: 'keywords', label: i18n.t('handoff_action:menu.keywords') },
      { id: 'ai_summary', label: i18n.t('handoff_action:menu.ai_summary') },
      { id: 'topic_routing', label: i18n.t('handoff_action:menu.topic_routing') },
      { id: 'email_notification', label: i18n.t('handoff_action:menu.email_notifications') },
      { id: 'need_help', label: i18n.t('handoff_action:menu.need_help') },
    ],
    [t],
  );

  const form = useForm<HandoffForm>({
    shouldFocusError: false,
    defaultValues: {
      availability: handoff.availability || [],
      generic_guidelines: handoff.generic_guidelines || [],
      custom_guidelines: handoff.custom_guidelines || [],
      routing_profiles: (handoff.routing_profiles || []).map((p) => ({
        user_id: p.user_id,
        routing_description: p.routing_description,
        is_active: p.is_active,
      })),
      settings: {
        question_message: handoff.settings.question_message || '',
        confirmation_message: handoff.settings.confirmation_message || '',
        confirmation_outside_working_hours_message:
          handoff.settings.confirmation_outside_working_hours_message || '',
        timezone: handoff.settings.timezone || '',
        is_active: handoff.settings.is_active || false,
        send_email_notification: handoff.settings.send_email_notification || false,
        ai_summary_active: handoff.settings.ai_summary_active || false,
        working_hours_active: handoff.settings.working_hours_active || false,
        keywords: handoff.settings.keywords.map((keyword) => ({ value: keyword })) || [],
        emails: handoff.settings.emails.map((keyword) => ({ value: keyword })) || [],
      },
    },
    resolver: zodResolver(handoffFormSchema),
  });

  const sheetOpen = isOpen(MODAL_NAMES.HANDOFF_ACTION_SHEET);
  const initializedRef = useRef(false);
  const routingMergedRef = useRef(false);

  useEffect(() => {
    if (!sheetOpen) {
      initializedRef.current = false;
      routingMergedRef.current = false;
      return;
    }

    // First pass on open: establish the full baseline immediately (routing_profiles is the
    // saved subset if scenario users haven't loaded yet) so a late reset can't clobber edits.
    if (!initializedRef.current) {
      initializedRef.current = true;
      routingMergedRef.current = !usersLoading && !!users?.length;
      form.reset(buildHandoffFormValues(handoff, users));
      return;
    }

    // Scenario users arrived after the initial reset: expand the routing baseline to every
    // user. keepDirtyValues preserves any edits made while users were still loading (and the
    // routing rows weren't rendered yet, so they can't have been edited).
    if (!routingMergedRef.current && !usersLoading && users?.length) {
      routingMergedRef.current = true;
      form.reset(buildHandoffFormValues(handoff, users), { keepDirtyValues: true });
    }
  }, [sheetOpen, usersLoading, users, handoff]);

  const { isActivated, isFetched: isSubscriptionFetched } = useSubscription();
  const handoffLocked = isSubscriptionFetched && !isActivated('human_handoff.active');

  const {
    formState: { isDirty },
  } = form;
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const mutation = useMutation<HandoffDto, unknown, HandoffDto>({
    mutationKey: [QueryKey.handoff.put],
    onSuccess: async (handoff) => {
      await queryClient.setQueryData([QueryKey.handoff.get], handoff);

      closeModal();
      toast.success(t('toast.update_successfully'));
    },
  });

  const handleSubmit = (values: HandoffForm) =>
    mutation.mutate({
      ...values,
      settings: {
        ...values.settings,
        keywords: values.settings.keywords.map((keyword) => keyword.value),
        emails: values.settings.emails.map((email) => email.value),
      },
    });

  const handleRequestClose = () => {
    if (isDirty && isActivated('human_handoff.active')) {
      setConfirmationModalOpen(true);
    } else {
      closeModal();
      form.reset({});
    }
  };

  const handleConfirmClose = () => {
    setConfirmationModalOpen(false);
    closeModal();
    form.reset({});
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleRequestClose();
      }
    },
    [handleRequestClose],
  );

  useEffect(() => {
    if (!isOpen(MODAL_NAMES.HANDOFF_ACTION_SHEET)) return;

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen(MODAL_NAMES.HANDOFF_ACTION_SHEET), handleKeyDown]);

  const handleSheetOpenChange = (open: boolean) => {
    if (!open) handleRequestClose();
  };

  const handleScheduleCall = () => openCalModal(CAL_LINK.productDemo);

  const { activeId, handleScrollToSection } = useActiveSection(
    sections.map((section) => section.id),
    scrollContainer,
  );

  useEffect(() => {
    if (!isOpen(MODAL_NAMES.HANDOFF_ACTION_SHEET)) return;

    const animationFrame = requestAnimationFrame(() => {
      if (scrollContainerRef.current) {
        setScrollContainer(scrollContainerRef.current);
      }
    });

    return () => cancelAnimationFrame(animationFrame);
  }, [isOpen(MODAL_NAMES.HANDOFF_ACTION_SHEET)]);

  useEffect(() => {
    const updateIndicator = () => {
      const container = containerRef.current;
      if (!container) return;

      const activeLink = container.querySelector(`[data-id="${activeId}"]`);
      if (activeLink instanceof HTMLElement) {
        const offset = activeLink.offsetTop + activeLink.offsetHeight - 36;
        setIndicatorTop(offset);
      }
    };

    updateIndicator();

    window.addEventListener('resize', updateIndicator);

    return () => {
      window.removeEventListener('resize', updateIndicator);
    };
  }, [activeId]);

  return (
    <TooltipProvider>
      <Form {...form}>
        <Sheet open={isOpen(MODAL_NAMES.HANDOFF_ACTION_SHEET)} onOpenChange={handleSheetOpenChange}>
          <SheetContent
            id={MODAL_NAMES.HANDOFF_ACTION_SHEET}
            className="!max-w-[100vw] xl:!w-[85%] xl:!max-w-7xl w-full pb-0 space-y-0"
            tabIndex={undefined}
            asChild
          >
            <form onSubmit={form.handleSubmit(handleSubmit)} className="h-full">
              <SheetHeader className="!flex-row !items-center !justify-between">
                <SheetTitle>{t('title')}</SheetTitle>
                <SheetDescription className="sr-only" aria-hidden="true">
                  Handoff Action
                </SheetDescription>
                <section className="flex items-center gap-2">
                  <Tooltip open={handoffLocked ? undefined : false}>
                    <TooltipTrigger asChild>
                      <div>
                        <Button type="submit" disabled={handoffLocked}>
                          {t('button.save')}
                        </Button>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent align="end">{t('tooltip.update_plan')}</TooltipContent>
                  </Tooltip>
                  <IconButton
                    onClick={() => handleSheetOpenChange(false)}
                    variant="secondary"
                    className="z-50"
                    data-testid="handoff-close-button"
                  >
                    <CloseIcon />
                  </IconButton>
                </section>
              </SheetHeader>

              <div className="grid grid-cols-1 xl:grid-cols-[0.2fr_auto_1fr] h-full overflow-hidden relative">
                <nav ref={containerRef} className="hidden xl:flex relative flex-col">
                  <motion.span
                    layout
                    layoutId="nav-indicator"
                    className="absolute left-0 w-[3px] h-[36px] bg-foreground"
                    style={{ top: indicatorTop }}
                    transition={{ type: 'tween', stiffness: 200, damping: 30 }}
                  />

                  <p className="text-foreground-subtle text-xs font-semibold mb-5">
                    {t('menu.overview')}
                  </p>

                  {sections.map(({ id, label }) => (
                    <a
                      key={id}
                      data-id={id}
                      onClick={() => handleScrollToSection(id)}
                      className={classNames(
                        'border-l border-border h-[36px] flex items-center relative text-sm text-left pl-4 cursor-pointer transition-colors duration-300 !no-underline',
                        {
                          'text-foreground font-semibold': activeId === id,
                          'text-muted-foreground font-medium': activeId !== id,
                        },
                      )}
                    >
                      {label}
                    </a>
                  ))}
                </nav>

                <Separator orientation="vertical" className="hidden xl:flex mr-4" />

                <section
                  ref={scrollContainerRef}
                  className="overflow-y-auto space-y-7 custom-scroll relative pl-1 pr-1"
                >
                  <Banner
                    Icon={UserIcon}
                    title={t('title')}
                    bgIconColor="bg-green-500"
                    description={t('description')}
                    className="grid !grid-cols-[1fr_0.2fr_auto] !items-start bg-muted p-7 rounded-xl"
                  >
                    <HandoffActionGraphic />
                    <FormField
                      name="settings.is_active"
                      render={({ field }) => (
                        <Switch
                          id="settings.is_active"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={handoffLocked}
                        />
                      )}
                    />
                  </Banner>

                  <div id="question">
                    <FormField
                      name="settings.question_message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('form.question_label')}</FormLabel>
                          <FormDescription>{t('form.question_description')}</FormDescription>
                          <FormControl>
                            <Input
                              autoFocus
                              placeholder={t('form.question_placeholder')}
                              data-testid="handoff-question-input"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Separator />

                  <div id="confirmation">
                    <FormField
                      name="settings.confirmation_message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('form.confirmation_label')}</FormLabel>
                          <FormDescription>{t('form.confirmation_description')}</FormDescription>
                          <FormControl>
                            <AutosizeTextarea
                              minHeight={100}
                              className="px-4"
                              placeholder={t('form.confirmation_placeholder')}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Separator />

                  <HumanAvailabilityField form={form} />

                  <Separator />

                  <HandoffRulesField form={form} />

                  <Separator />

                  <KeywordsField form={form} />

                  {/* <Separator /> */}

                  <div id="ai_summary">
                    <Banner
                      className="!items-start"
                      Icon={StarsIcon}
                      bgIconColor="bg-yellow-500"
                      title={t('form.ai_summary_label')}
                      description={t('form.ai_summary_description')}
                    >
                      <FormField
                        name="settings.ai_summary_active"
                        render={({ field }) => (
                          <Switch
                            id="settings.ai_summary_active"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        )}
                      />
                    </Banner>
                  </div>

                  <Separator />

                  <TopicRoutingField form={form} />

                  <Separator />

                  <EmailNotificationField form={form} />

                  <Separator />

                  <div id="need_help">
                    <div className="flex py-[12vh] xl:py-[26vh] space-y-2 flex-col items-center">
                      <img
                        src="/static/assets/qc-smart-data.webp"
                        alt="AI Insights graphic"
                        className="w-64 h-64 object-cover"
                      />
                      <p className="font-medium">{t('need_help.title')}</p>
                      <p className="text-sm text-foreground-subtle text-center max-w-xl">
                        <Trans
                          t={t}
                          i18nKey={'need_help.description'}
                          components={{
                            docs_link: (
                              <a
                                href="https://docs.quickchat.ai/"
                                className="font-medium text-foreground"
                                target="_blank"
                                rel="noreferrer"
                              />
                            ),
                          }}
                        />
                      </p>
                      <Button variant="secondary" onClick={handleScheduleCall} className="mt-4">
                        <Calendar className="size-4" />
                        {t('button.schedule_a_call')}
                      </Button>
                    </div>
                  </div>
                </section>
              </div>

              <DialogPrimitive.Root
                open={confirmationModalOpen}
                onOpenChange={setConfirmationModalOpen}
              >
                <DialogPrimitive.Overlay className="!mt-0 absolute inset-0 bg-black/30 backdrop-blur-sm z-20" />

                <DialogPrimitive.Content
                  className="absolute inset-0 flex items-center justify-center p-0 z-30 focus:outline-none"
                  role="alertdialog"
                  aria-modal="true"
                >
                  <div className="space-y-4 bg-card rounded-lg p-6 max-w-md w-full shadow-lg">
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        <CircleAlert className="inline size-4 bg-red-100 rounded-lg text-red-500 p-2 box-content" />
                        {t('close_modal.title')}
                      </AlertDialogTitle>
                    </AlertDialogHeader>

                    <AlertDialogDescription>{t('close_modal.subtitle')}</AlertDialogDescription>

                    <Separator className="my-7" />
                    <AlertDialogFooter>
                      <AlertDialogCancel asChild>
                        <Button size="md" variant="secondary">
                          {t('close_modal.button.cancel')}
                        </Button>
                      </AlertDialogCancel>

                      <Button variant="danger" size="md" onClick={handleConfirmClose}>
                        {t('close_modal.button.continue')}
                      </Button>
                    </AlertDialogFooter>
                  </div>
                </DialogPrimitive.Content>
              </DialogPrimitive.Root>
            </form>
          </SheetContent>
        </Sheet>
      </Form>
    </TooltipProvider>
  );
};
